import { Component, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AuthenticationService, CreateRedirectLinkLoginReq } from '@b3networks/api/auth';
import { Partner, PartnerQuery } from '@b3networks/api/partner';
import { VoucherService } from '@b3networks/api/payment';
import { ToastService } from '@b3networks/shared/ui/toast';
import { catchError, combineLatest, map, Observable, of } from 'rxjs';
import { finalize } from 'rxjs/operators';

export enum SignUpState {
  'Default' = 'Default',
  'SignUpEmail' = 'SignUpEmail'
}

export interface SignUpData {
  partner: Partner;
  bundleHasSGNumber: boolean;
}

export enum SignUpType {
  SingPass = 'SingPass',
  CorpPass = 'CorpPass'
}

@Component({
  selector: 'b3n-sign-up',
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss']
})
export class SignUpComponent implements OnInit {
  readonly SignUpType = SignUpType;
  readonly SignUpState = SignUpState;

  creatingLink: { [key in SignUpType]: boolean } = {
    [SignUpType.SingPass]: false,
    [SignUpType.CorpPass]: false
  };

  data$: Observable<SignUpData>;
  redirectUrl: string;
  orderToken: string;
  orgUuid: string;

  state = SignUpState.Default;
  emailCtrl: FormControl;
  codeCtrl: FormControl;

  constructor(
    private partnerQuery: PartnerQuery,
    private authenticationService: AuthenticationService,
    private route: ActivatedRoute,
    private router: Router,
    private voucherService: VoucherService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.extractParams(params);
      this.initData();
    });

    this.initializeFormControls();
  }

  signup(type: SignUpType) {
    this.creatingLink[type] = true;
    const createOrgUrl = this.createOrganizationUrl(this.redirectUrl);
    const req = {
      originUrl: createOrgUrl,
      type: type
    } as CreateRedirectLinkLoginReq;

    this.authenticationService
      .createRedirectLinkForLogin(req)
      .pipe(finalize(() => (this.creatingLink[type] = false)))
      .subscribe({
        next: url => this.handleRedirect(url),
        error: error => this.handleError(error)
      });
  }

  navigate2Login() {
    this.router.navigate(['auth', 'login'], { queryParamsHandling: 'merge' });
  }

  private extractParams(params: Params): void {
    if (params['redirectUrl'] || params['redirectUri']) {
      this.redirectUrl = decodeURIComponent(params['redirectUrl'] || params['redirectUri']);
      const urlParams = this.getParamFromUrl(this.redirectUrl);
      this.orderToken = urlParams?.orderToken;
      this.orgUuid = urlParams?.orgUuid;
    }
  }

  private initializeFormControls(): void {
    this.emailCtrl = new FormControl(null, [Validators.required, Validators.email]);
    this.codeCtrl = new FormControl(null, [Validators.required]);
  }

  private createOrganizationUrl(redirectUrl?: string): string {
    let url = `${location.origin}/redeem/#/create-org`;
    if (redirectUrl) {
      url += `?redirectUrl=${encodeURIComponent(redirectUrl)}`;
    }
    return url;
  }

  private getParamFromUrl(url) {
    return url
      ?.split('?')[1]
      ?.split('&')
      ?.reduce((obj, s) => {
        const arrs = s.split('=');
        obj[arrs[0]] = arrs[1];
        return obj;
      }, {});
  }

  private handleRedirect(url: string): void {
    window.location.replace(url);
  }

  private handleError(error): void {
    if (error?.code === 'auth.MyInfoVerificationUnavailable') {
      this.router.navigate(['auth', 'block-signup']);
    } else {
      this.toastService.error(error.message);
    }
  }

  private initData() {
    this.data$ = combineLatest([
      this.partnerQuery.partner$,
      this.voucherService.getOrderInfo(this.orderToken, this.orgUuid).pipe(catchError(_ => of(null)))
    ]).pipe(
      map(([partner, paymentIntent]) => <SignUpData>{ partner: partner, bundleHasSGNumber: paymentIntent?.hasSGNumber })
    );
  }
}
